<template>
  <div>
    <!-- Cabeçalho -->
    <div class="vm-page-header">
      <div>
        <h1>Consulta encerrada</h1>
        <span class="vm-page-header__sub" v-if="roomData">
          {{ roomData.patient.name }} · consulta encerrada
        </span>
        <span class="vm-page-header__sub" v-else>Atendimento concluído</span>
      </div>
      <div class="vm-page-header__actions">
        <button class="btn btn--accent btn--sm" @click="pushToDashboard">
          Voltar ao dashboard
        </button>
      </div>
    </div>

    <div class="cf-body">
      <div class="cf-grid">
        <!-- Coluna esquerda: documentos + anotações -->
        <div class="card" style="padding:var(--space-5)">
          <!-- Linha de sucesso -->
          <div
            class="row"
            style="gap:var(--space-3);padding-bottom:var(--space-4);border-bottom:1px solid var(--color-gray-100)"
          >
            <div
              class="avatar avatar--md"
              style="background:var(--color-success-light);color:var(--color-success)"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="stack" style="gap:1px">
              <span
                style="font-weight:var(--fw-bold);font-size:var(--font-size-lg);color:var(--color-gray-900)"
              >
                Atendimento concluído
              </span>
              <span
                style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
              >
                Documentos enviados ao paciente
              </span>
            </div>
          </div>

          <!-- Documentos emitidos -->
          <span class="fieldset-title" style="margin-top:var(--space-4)">
            Documentos emitidos
          </span>
          <div
            class="stack"
            style="gap:var(--space-2);margin-top:var(--space-3)"
          >
            <!-- Receita(s) emitida(s) -->
            <a
              v-for="p in prescriptions"
              :key="p.id"
              class="card cf-prescription"
              :href="p.links || undefined"
              target="_blank"
              rel="noopener"
              style="padding:var(--space-3) var(--space-4);box-shadow:none;border-color:var(--color-gray-100);display:flex;align-items:center;gap:var(--space-3);text-decoration:none"
            >
              <div
                class="avatar avatar--sm"
                style="background:var(--color-accent-light);color:var(--color-accent)"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 2h6M12 2v4M5 8h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 8z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
                >
                  Receita · {{ prescriptionSummary(p) }}
                </span>
                <span
                  :style="{
                    'font-size': 'var(--font-size-xs)',
                    color: isPrescriptionSigned(p)
                      ? 'var(--color-gray-400)'
                      : 'var(--color-danger)'
                  }"
                >
                  {{
                    isPrescriptionSigned(p)
                      ? "Memed · assinada digitalmente"
                      : "Receita não assinada digitalmente"
                  }}
                </span>
              </div>
              <span
                class="badge"
                :class="
                  isPrescriptionSigned(p) ? 'badge--success' : 'badge--warning'
                "
              >
                {{ isPrescriptionSigned(p) ? "Emitida" : "Não assinada" }}
              </span>
            </a>

            <!-- Estado vazio de receita -->
            <div
              v-if="!prescriptions.length"
              class="card"
              style="padding:var(--space-3) var(--space-4);box-shadow:none;border-color:var(--color-gray-100);display:flex;align-items:center;gap:var(--space-3)"
            >
              <div
                class="avatar avatar--sm"
                style="background:var(--color-accent-light);color:var(--color-accent)"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 2h6M12 2v4M5 8h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 8z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
                >
                  Receita médica
                </span>
                <span
                  style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                >
                  Nenhuma receita emitida nesta consulta
                </span>
              </div>
              <span class="badge badge--neutral">Nenhuma emitida</span>
            </div>
            <!-- Atestado -->
            <div
              class="card"
              style="padding:var(--space-3) var(--space-4);box-shadow:none;border-color:var(--color-gray-100);display:flex;align-items:center;gap:var(--space-3)"
            >
              <div
                class="avatar avatar--sm"
                style="background:var(--color-primary-light);color:var(--color-primary)"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                  <path d="M14 2v6h6" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
                >
                  Atestado médico
                </span>
                <span
                  style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                >
                  Documento médico
                </span>
              </div>
              <span v-if="atestados.length" class="badge badge--success">
                {{
                  atestados.length === 1
                    ? "Emitido"
                    : `${atestados.length} emitidos`
                }}
              </span>
              <span v-else class="badge badge--neutral">Nenhum emitido</span>
            </div>

            <!-- Lista de atestados emitidos: editar / anular -->
            <div
              v-for="cert in atestados"
              :key="cert.id"
              class="card"
              style="padding:var(--space-3) var(--space-4);box-shadow:none;border-color:var(--color-gray-100);margin-top:var(--space-2)"
            >
              <template v-if="editingCertificateId === cert.id">
                <div class="stack" style="gap:var(--space-2)">
                  <input
                    v-model="certificateForm.description"
                    class="input"
                    placeholder="Descrição"
                  />
                  <input
                    v-model.number="certificateForm.days_off"
                    class="input"
                    type="number"
                    min="0"
                    placeholder="Dias de afastamento"
                  />
                  <input
                    v-model="certificateForm.cid_code"
                    class="input"
                    placeholder="CID"
                  />
                  <div style="display:flex;gap:var(--space-2)">
                    <button
                      class="btn btn--primary btn--sm"
                      :disabled="savingCertificate"
                      @click="saveCertificate(cert)"
                    >
                      Salvar
                    </button>
                    <button
                      class="btn btn--ghost btn--sm"
                      :disabled="savingCertificate"
                      @click="cancelEditCertificate"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </template>
              <div
                v-else
                style="display:flex;align-items:center;gap:var(--space-3)"
              >
                <div class="stack" style="gap:0;flex:1">
                  <span
                    style="font-size:var(--font-size-sm);color:var(--color-gray-900)"
                  >
                    {{ cert.description || "Atestado médico" }}
                  </span>
                  <span
                    style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                  >
                    {{ cert.days_off ? `${cert.days_off} dia(s)` : "" }}
                    {{ cert.cid_code ? `· CID ${cert.cid_code}` : "" }}
                  </span>
                </div>
                <button
                  class="btn btn--ghost btn--sm"
                  @click="startEditCertificate(cert)"
                >
                  Editar
                </button>
                <button
                  class="btn btn--outline btn--sm"
                  @click="annulCertificate(cert)"
                >
                  Anular
                </button>
              </div>
            </div>
          </div>

          <!-- Anotações da consulta -->
          <span class="fieldset-title" style="margin-top:var(--space-5)">
            Anotações da consulta
          </span>
          <div
            v-if="appointmentNotes.length === 0"
            class="cf-notes cf-notes--empty"
          >
            Sem anotações registradas para esta consulta.
          </div>
          <div v-else class="cf-notes-list">
            <div
              v-for="note in appointmentNotes"
              :key="note.id"
              class="cf-note"
            >
              <span class="cf-note__date">
                {{ formatNoteDate(note) }}
              </span>
              <p class="cf-note__text" v-html="note.description" />
            </div>
          </div>
        </div>

        <!-- Coluna direita: financeiro + próximo paciente -->
        <div class="stack" style="gap:var(--space-4)">
          <!-- Recebimento desta consulta -->
          <div
            class="card"
            style="padding:var(--space-6);border-left:3px solid var(--color-success)"
          >
            <span class="fieldset-title">Recebimento desta consulta</span>
            <div class="breakdown" style="margin-top:var(--space-3)">
              <div class="br">
                Valor bruto
                <b class="money">
                  {{ earnings ? intToBRL(earnings.gross_cents) : "—" }}
                </b>
              </div>
              <div class="br">
                Taxa{{
                  earnings ? ` (${100 - earnings.comission_percent}%)` : ""
                }}
                <b class="money" style="color:var(--color-danger)">
                  {{ earnings ? intToBRL(earnings.platform_cents) : "—" }}
                </b>
              </div>
              <div class="br br--total">
                Seu líquido
                <b class="money money--pos">
                  {{ earnings ? intToBRL(earnings.doctor_cents) : "—" }}
                </b>
              </div>
            </div>
            <router-link
              to="/financeiro"
              class="btn btn--outline btn--block"
              style="margin-top:var(--space-4);text-decoration:none"
            >
              Ver no financeiro →
            </router-link>
          </div>

          <!-- Próximo paciente -->
          <div
            class="card"
            style="padding:var(--space-5)"
            v-if="nextAppointment"
          >
            <span class="fieldset-title">Próximo paciente</span>
            <div
              class="row"
              style="gap:var(--space-3);margin-top:var(--space-3)"
            >
              <div class="avatar avatar--sm">
                {{ nextPatientInitials }}
              </div>
              <div class="stack" style="gap:0;flex:1">
                <span
                  style="font-size:var(--font-size-sm);font-weight:var(--fw-bold);color:var(--color-gray-900)"
                >
                  {{ nextPatientName }}
                </span>
                <span
                  class="num"
                  style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
                >
                  {{ nextAppointmentTime }}
                </span>
              </div>
            </div>
            <router-link
              to="/resumo-da-agenda"
              class="btn btn--ghost btn--block btn--sm"
              style="margin-top:var(--space-3);text-decoration:none"
            >
              Ver agenda
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { pt as locale } from "date-fns/locale";
import { Prescriptions } from "../services/prescriptions";
import { MedicalCertificates } from "../services/medical_certificates";
import { Appointments } from "../services/appointments";
import { intToBRL } from "../utils/currency/convert";

export default {
  name: "ConsultFinished",

  data() {
    return {
      roomData: null,
      prescriptions: [],
      atestados: [],
      editingCertificateId: null,
      certificateForm: { description: "", days_off: null, cid_code: "" },
      savingCertificate: false,
      earnings: null,
      appointmentNotes: []
    };
  },

  mounted() {
    const roomId = this.$route.params.room;
    if (roomId) {
      this.$store
        .dispatch("room/getRoomDetailsV2", roomId)
        .then(response => {
          this.roomData = response.data;
          this.loadPrescriptions();
          this.loadEarnings();
          this.loadNotes();
          return this.reloadCertificates();
        })
        .catch(() => {});
    }
    this.$store.dispatch("appointments/fetchFutureAppointments");
  },

  methods: {
    intToBRL,
    pushToDashboard() {
      this.$router.push({ name: "Dashboard" });
    },
    loadEarnings() {
      const appointmentId = this.roomData && this.roomData.appointment_id;
      if (!appointmentId) return;
      Appointments.earnings(appointmentId)
        .then(res => {
          this.earnings = res.data;
        })
        .catch(() => {});
    },
    loadNotes() {
      const appointmentId = this.roomData && this.roomData.appointment_id;
      if (!appointmentId) return;
      Appointments.fetchNotes(appointmentId)
        .then(res => {
          this.appointmentNotes = res.data || [];
        })
        .catch(() => {});
    },
    loadPrescriptions() {
      const appointmentId = this.roomData && this.roomData.appointment_id;
      if (!appointmentId) return;
      Prescriptions.fetchDoctorPrescriptions(appointmentId)
        .then(res => {
          this.prescriptions = res.data || [];
        })
        .catch(() => {});
    },
    reloadCertificates() {
      const appointmentId = this.roomData && this.roomData.appointment_id;
      if (!appointmentId) return Promise.resolve();
      return MedicalCertificates.listAppointmentMedicalCertificates(
        appointmentId
      )
        .then(res => {
          this.atestados = res.data || [];
        })
        .catch(() => {});
    },
    startEditCertificate(cert) {
      this.editingCertificateId = cert.id;
      this.certificateForm = {
        description: cert.description || "",
        days_off: cert.days_off,
        cid_code: cert.cid_code || ""
      };
    },
    cancelEditCertificate() {
      this.editingCertificateId = null;
    },
    saveCertificate(cert) {
      const appointmentId = this.roomData && this.roomData.appointment_id;
      if (!appointmentId) return;
      this.savingCertificate = true;
      MedicalCertificates.updateMedicalCertificate(
        appointmentId,
        cert.id,
        this.certificateForm
      )
        .then(() => {
          this.editingCertificateId = null;
          return this.reloadCertificates();
        })
        .catch(() => {})
        .finally(() => {
          this.savingCertificate = false;
        });
    },
    annulCertificate(cert) {
      const appointmentId = this.roomData && this.roomData.appointment_id;
      if (!appointmentId) return;
      if (!window.confirm("Deseja realmente anular este atestado?")) return;
      MedicalCertificates.deleteMedicalCertificate(appointmentId, cert.id)
        .then(() => this.reloadCertificates())
        .catch(() => {});
    },
    prescriptionSummary(p) {
      const names = (p.drugs || []).map(d => d.name).filter(Boolean);
      return names.length ? names.join(", ") : "Memed · assinada digitalmente";
    },
    isPrescriptionSigned(p) {
      return p.signed !== false;
    },
    formatNoteDate(note) {
      return format(parseISO(note.created_at), "dd/MM/yyyy 'às' HH:mm", {
        locale
      });
    }
  },

  computed: {
    nextAppointment() {
      const list = this.$store.state.appointments.futureAppointmentsList || [];
      return list[0] || null;
    },
    nextPatientName() {
      return (
        this.nextAppointment?.patient?.name || this.nextAppointment?.name || ""
      );
    },
    nextPatientInitials() {
      return this.nextPatientName.substring(0, 2).toUpperCase() || "?";
    },
    nextAppointmentTime() {
      if (!this.nextAppointment?.scheduled_at) return "";
      try {
        return format(
          parseISO(this.nextAppointment.scheduled_at),
          "HH:mm · d/MM",
          { locale }
        );
      } catch {
        return "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cf-body {
  padding: var(--space-8);
  max-width: 1000px;
}

.cf-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.cf-notes {
  margin-top: var(--space-2);
  min-height: 80px;
  width: 100%;
  background: var(--color-gray-50);
  border: 1.5px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  font-family: inherit;
  color: var(--color-gray-600);
  line-height: 1.6;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
}

.cf-notes-list {
  margin-top: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.cf-note {
  padding: var(--space-3) var(--space-4);
  background: var(--color-gray-50);
  border: 1.5px solid var(--color-gray-200);
  border-radius: var(--radius-md);
}

.cf-note__date {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-400);
  margin-bottom: var(--space-1);
}

.cf-note__text {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
  line-height: 1.6;
  word-break: break-word;
}
</style>
